import { ref, onMounted, onUnmounted } from 'vue'

/**
 * Composable for GeoGebra applet integration
 * Wraps the GeoGebra API and provides Vue 3 reactive interface
 */
export function useGeoGebra() {
  const appletInstance = ref(null)
  const isReady = ref(false)
  const isLoading = ref(false)
  const error = ref(null)

  /**
   * Initialize GeoGebra applet
   * @param {string} containerId - DOM container ID or class
   * @param {object} config - GeoGebra configuration
   * @param {Function} onLoad - Callback when applet is loaded
   */
  const initApplet = (containerId, config, onLoad = null) => {
    return new Promise((resolve, reject) => {
      if (typeof window.GGBApplet === 'undefined') {
        error.value = 'GeoGebra library (deployggb.js) not loaded'
        reject(new Error(error.value))
        return
      }

      isLoading.value = true
      error.value = null

      try {
        const parameters = {
          detachKeyboard: config.detachKeyboard ?? false,
          appName: config.appName || 'classic',
          width: config.width || 900,
          height: config.height || 600,
          showToolBar: config.showToolBar ?? false,
          showMenuBar: config.showMenuBar ?? false,
          showAlgebraInput: config.showAlgebraInput ?? true,
          algebraInputPosition: config.algebraInputPosition ?? true,
          showFullscreenButton: config.showFullscreenButton ?? true,
          enableShiftDragZoom: config.enableShiftDragZoom ?? true,
          showZoomButtons: config.showZoomButtons ?? true,
          allowStyleBar: config.allowStyleBar ?? false,
          enableRightClick: config.enableRightClick ?? false,
          enableUndoRedo: config.enableUndoRedo ?? true,
          enableLabelDrags: config.enableLabelDrags ?? true,
          enableFileFeatures: config.enableFileFeatures ?? false,
          language: config.language || 'en',
          borderColor: config.borderColor || 'teal',
          showLogging: false,
          errorDialogsActive: false,
          useBrowserForJS: false,
          appletOnLoad: (api) => {
            appletInstance.value = api
            isReady.value = true
            isLoading.value = false

            if (onLoad) onLoad(api)
            resolve(api)
          }
        }

        // Add base64 data if provided
        if (config.ggbbase64 && config.ggbbase64.length > 0) {
          parameters.ggbbase64 = config.ggbbase64
        }

        // Add filename/URL if provided
        if (config.filename && config.filename.length > 0) {
          parameters.filename = config.filename
        }

        // Add material_id if provided (GeoGebra project ID)
        if (config.material_id) {
          parameters.material_id = config.material_id
        }

        const ggbApp = new window.GGBApplet(parameters, true)
        ggbApp.inject(containerId)
      } catch (err) {
        error.value = err.message
        isLoading.value = false
        reject(err)
      }
    })
  }

  /**
   * Load GeoGebra file from base64
   */
  const loadFromBase64 = (base64String) => {
    if (!appletInstance.value) {
      console.error('Applet not initialized')
      return
    }
    appletInstance.value.setBase64(base64String)
  }

  /**
   * Get current state as base64
   */
  const getBase64 = () => {
    return new Promise((resolve) => {
      if (!appletInstance.value) {
        resolve('')
        return
      }
      appletInstance.value.getBase64((base64) => {
        resolve(base64)
      })
    })
  }

  /**
   * Get value of a GeoGebra variable
   */
  const getValue = (varName) => {
    if (!appletInstance.value) return null
    try {
      return appletInstance.value.getValue(varName)
    } catch {
      return null
    }
  }

  /**
   * Get string value of a GeoGebra variable
   */
  const getValueString = (varName) => {
    if (!appletInstance.value) return ''
    try {
      return appletInstance.value.getValueString(varName)
    } catch {
      return ''
    }
  }

  /**
   * Set value of a GeoGebra variable
   */
  const setValue = (varName, value) => {
    if (!appletInstance.value) return
    appletInstance.value.setValue(varName, value)
  }

  /**
   * Register click listener
   */
  const registerClickListener = (callback) => {
    if (!appletInstance.value) return
    appletInstance.value.registerClickListener(callback)
  }

  /**
   * Register update listener
   */
  const registerUpdateListener = (callback) => {
    if (!appletInstance.value) return
    appletInstance.value.registerUpdateListener(callback)
  }

  /**
   * Remove applet instance
   */
  const removeApplet = () => {
    if (appletInstance.value) {
      try {
        appletInstance.value.remove()
      } catch (err) {
        console.error('Error removing GeoGebra applet:', err)
      }
      appletInstance.value = null
      isReady.value = false
    }
  }

  /**
   * Show/hide menu bar
   */
  const showMenuBar = (show) => {
    if (!appletInstance.value) return
    appletInstance.value.showMenuBar(show)
  }

  /**
   * Show/hide tool bar
   */
  const showToolBar = (show) => {
    if (!appletInstance.value) return
    appletInstance.value.showToolBar(show)
  }

  /**
   * Enable/disable right click
   */
  const enableRightClick = (enable) => {
    if (!appletInstance.value) return
    appletInstance.value.enableRightClick(enable)
  }

  /**
   * Reset applet to initial state
   */
  const reset = () => {
    if (!appletInstance.value) return
    appletInstance.value.reset()
  }

  /**
   * Load GeoGebra project from GeoGebra.org
   * @param {string} projectId - GeoGebra project ID (e.g., "kUmRhNnN")
   */
  const loadFromProjectId = async (projectId) => {
    try {
      const response = await fetch(
        `https://api.geogebra.org/v1.0/worksheets/${projectId}?scope=basic&embed=actions`
      )
      const data = await response.json()

      // Find the GGB file URL
      let ggbUrl = null
      for (const element of data.elements || []) {
        if (element.url) {
          ggbUrl = element.url.replace(/([^:]\/)\/+/g, '$1')
          break
        }
      }

      return {
        url: ggbUrl,
        thumbnail: data.thumbUrl,
        title: data.title
      }
    } catch (err) {
      console.error('Error loading GeoGebra project:', err)
      throw err
    }
  }

  // Cleanup on unmount
  onUnmounted(() => {
    removeApplet()
  })

  return {
    // State
    appletInstance,
    isReady,
    isLoading,
    error,

    // Methods
    initApplet,
    loadFromBase64,
    getBase64,
    getValue,
    getValueString,
    setValue,
    registerClickListener,
    registerUpdateListener,
    removeApplet,
    showMenuBar,
    showToolBar,
    enableRightClick,
    reset,
    loadFromProjectId
  }
}
